import { useState, useEffect } from "react";
import { Users, Plus, X, ChevronRight, ShieldOff, FileLock2, CalendarClock, Building2, CheckCircle2, Clock, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { DocumentsPanel } from "@/components/DocumentsPanel";
import { MeetingsPanel } from "@/components/MeetingsPanel";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const empty = { full_name: "", email: "", company: "", password: "" };
  const [form, setForm] = useState(empty);

  const load = async () => {
    try { const { data } = await api.get("/admin/clients"); setClients(data); }
    catch { toast.error("Failed to load clients"); }
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/clients", form);
      toast.success("Client account created");
      setForm(empty); setShowForm(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create client"); }
    finally { setSaving(false); }
  };

  const resetMfa = async (c) => {
    try { await api.post(`/admin/clients/${c.id}/reset-mfa`); toast.success(`MFA reset for ${c.full_name}`); load(); }
    catch { toast.error("Failed to reset MFA"); }
  };

  const removeClient = async (c) => {
    if (!window.confirm(`Permanently delete ${c.full_name} and all of their documents and meetings? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/clients/${c.id}`);
      toast.success(`${c.full_name} deleted`);
      if (selected?.id === c.id) setSelected(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to delete client"); }
  };

  const inputCls = "w-full bg-[#0d0d0d] border border-white/12 rounded-lg px-4 py-2.5 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent";

  if (selected) {
    return (
      <div>
        <button onClick={() => { setSelected(null); load(); }} className="text-zinc-500 hover:text-white text-sm mb-6" data-testid="back-to-clients">← All clients</button>
        <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
          <div>
            <h1 className="font-dash text-3xl">{selected.full_name}</h1>
            <p className="text-zinc-500 text-sm mt-1">{selected.email}{selected.company ? ` · ${selected.company}` : ""}</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {selected.approvals?.pending > 0 && (
              <span className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-amber-500/30 text-amber-400"><Clock size={12} /> {selected.approvals.pending} pending</span>
            )}
            {selected.approvals?.approved > 0 && (
              <span className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-emerald-500/30 text-emerald-400"><CheckCircle2 size={12} /> {selected.approvals.approved} approved</span>
            )}
            <span className={`text-xs px-3 py-1.5 rounded-full border ${selected.mfa_enabled ? "border-emerald-500/30 text-emerald-400" : "border-amber-500/30 text-amber-400"}`}>
              {selected.mfa_enabled ? "MFA enrolled" : "MFA pending"}
            </span>
          </div>
        </div>
        <Tabs defaultValue="documents">
          <TabsList className="bg-[#0d0d0d] border border-white/10">
            <TabsTrigger value="documents" data-testid="tab-documents" className="data-[state=active]:bg-white/10"><FileLock2 size={15} className="mr-2" /> Documents</TabsTrigger>
            <TabsTrigger value="meetings" data-testid="tab-meetings" className="data-[state=active]:bg-white/10"><CalendarClock size={15} className="mr-2" /> Meetings</TabsTrigger>
          </TabsList>
          <TabsContent value="documents" className="mt-8"><DocumentsPanel clientId={selected.id} isAdmin /></TabsContent>
          <TabsContent value="meetings" className="mt-8"><MeetingsPanel clientId={selected.id} isAdmin /></TabsContent>
        </Tabs>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="overline mb-2">Administration</div>
          <h1 className="font-dash text-3xl">Clients</h1>
          <p className="text-zinc-500 mt-2 text-sm">Create accounts and manage documents, meetings and fees for each client.</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} data-testid="add-client-btn" className="btn-metal px-5 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2">
          {showForm ? <X size={15} /> : <Plus size={15} />} {showForm ? "Close" : "New client"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={create} data-testid="client-form" className="rounded-xl border border-white/10 bg-[#0b0b0b] p-6 mb-8 grid sm:grid-cols-2 gap-4">
          <input required className={inputCls} placeholder="Full name" value={form.full_name} onChange={set("full_name")} data-testid="client-name" />
          <input required type="email" className={inputCls} placeholder="Email" value={form.email} onChange={set("email")} data-testid="client-email" />
          <input className={inputCls} placeholder="Company / firm (optional)" value={form.company} onChange={set("company")} data-testid="client-company" />
          <input required type="text" className={inputCls} placeholder="Temporary password" value={form.password} onChange={set("password")} data-testid="client-password" />
          <button type="submit" disabled={saving} className="btn-metal px-6 py-2.5 rounded-lg text-sm font-medium sm:col-span-2 disabled:opacity-60" data-testid="client-save">{saving ? "Creating…" : "Create account"}</button>
        </form>
      )}

      {clients.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/12 py-16 text-center text-zinc-500" data-testid="clients-empty">
          <Users size={28} strokeWidth={1} className="mx-auto mb-3 opacity-60" /> No clients yet.
        </div>
      ) : (
        <div className="rounded-xl border border-white/10 overflow-hidden divide-y divide-white/8" data-testid="clients-list">
          {clients.map((c) => (
            <div key={c.id} className="flex items-center gap-4 px-5 py-4 bg-[#0b0b0b] hover:bg-[#101010] transition-colors" data-testid={`client-row-${c.id}`}>
              <div className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-zinc-300 text-sm font-medium shrink-0">
                {c.full_name?.charAt(0) || "?"}
              </div>
              <div className="min-w-0 flex-1 cursor-pointer" onClick={() => setSelected(c)}>
                <div className="text-white text-sm truncate">{c.full_name}</div>
                <div className="text-zinc-500 text-xs truncate flex items-center gap-2">
                  {c.email}{c.company && <span className="flex items-center gap-1"><Building2 size={11} /> {c.company}</span>}
                </div>
              </div>
              <span className={`hidden sm:inline text-xs px-2.5 py-1 rounded-full border ${c.mfa_enabled ? "border-emerald-500/30 text-emerald-400" : "border-amber-500/30 text-amber-400"}`}>
                {c.mfa_enabled ? "MFA on" : "MFA pending"}
              </span>
              {c.approvals?.pending > 0 && (
                <span className="hidden md:inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border border-amber-500/30 text-amber-400" data-testid={`approvals-pending-${c.id}`}>
                  <Clock size={11} /> {c.approvals.pending} pending
                </span>
              )}
              {c.approvals?.approved > 0 && (
                <span className="hidden md:inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border border-emerald-500/30 text-emerald-400" data-testid={`approvals-approved-${c.id}`}>
                  <CheckCircle2 size={11} /> {c.approvals.approved} approved
                </span>
              )}
              <button onClick={() => resetMfa(c)} title="Reset MFA" className="p-2 text-zinc-500 hover:text-amber-400 transition-colors" data-testid={`reset-mfa-${c.id}`}><ShieldOff size={16} /></button>
              <button onClick={() => removeClient(c)} title="Delete client" className="p-2 text-zinc-500 hover:text-red-400 transition-colors" data-testid={`delete-client-${c.id}`}><Trash2 size={16} /></button>
              <button onClick={() => setSelected(c)} className="p-2 text-zinc-400 hover:text-white" data-testid={`open-client-${c.id}`}><ChevronRight size={18} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
